/**
 * The scenery outside the train window, as tiling SVG backgrounds.
 *
 * Drawn here rather than shipped as images: they are seeded shapes, they tile
 * seamlessly, and there are no people in any of them, only buildings, hills,
 * lamps and poles. The lights in the buildings are drawn on sheets of their
 * own, so the window can turn them up as the evening gets later.
 */

function rng(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const uri = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
const svg = (w: number, h: number, body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;

/**
 * A drawing in two sheets of the same size: the silhouettes, and the lights
 * in them. The lights go on a layer of their own so the window can switch
 * them on as dusk turns to night.
 */
class Sheet {
  body = '';
  lit = '';
  rect(x: number, y: number, w: number, h: number, fill: string, extra = '') {
    this.body += `<rect x="${x.toFixed(0)}" y="${y.toFixed(0)}" width="${w.toFixed(0)}" height="${h.toFixed(0)}" fill="${fill}"${extra}/>`;
  }
  poly(points: [number, number][], fill: string) {
    this.body += `<polygon points="${points.map(([x, y]) => `${x.toFixed(0)},${y.toFixed(0)}`).join(' ')}" fill="${fill}"/>`;
  }
  light(x: number, y: number, w: number, h: number, fill: string, opacity: number) {
    this.lit += `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" fill="${fill}" opacity="${opacity.toFixed(2)}"/>`;
  }
  glow(x: number, y: number, r: number, fill: string) {
    this.lit += `<circle cx="${x.toFixed(0)}" cy="${y.toFixed(0)}" r="${(r * 3).toFixed(0)}" fill="${fill}" opacity=".13"/><circle cx="${x.toFixed(0)}" cy="${y.toFixed(0)}" r="${r.toFixed(1)}" fill="${fill}"/>`;
  }
  done(w: number, h: number) {
    return { body: uri(svg(w, h, this.body)), lit: uri(svg(w, h, this.lit)) };
  }
}

const WARM = ['#ffd48a', '#ffc872', '#ffe2a8'];
const COOL = ['#cfe6ff', '#bfe3ff', '#e8f1ff'];
const NEON = ['#ff5aa0', '#4de2ff', '#ffb01f', '#9cff6a', '#ff6a4d'];
const pickOf = <T>(r: () => number, xs: T[]) => xs[Math.floor(r() * xs.length)]!;

/** Windows on a block face, a share of them lit. */
function windows(sh: Sheet, r: () => number, x: number, top: number, w: number, bottom: number,
  { dx = 16, dy = 22, ww = 6, wh = 9, odds = 0.18, tone = WARM } = {}) {
  for (let wy = top + 12; wy < bottom - 10; wy += dy) {
    for (let wx = x + 7; wx < x + w - ww - 4; wx += dx) {
      if (r() < odds) sh.light(wx, wy, ww, wh, pickOf(r, tone), 0.45 + r() * 0.5);
    }
  }
}

/**
 * The far city: two rows of towers, no two alike. Stepped tops, spires,
 * masts with red aircraft lights, slanted roofs, and once per tile a lattice
 * tower standing over the rest.
 */
function skyline() {
  const r = rng(11);
  const W = 2400;
  const H = 420;
  const sh = new Sheet();
  // the back row, paler with distance
  for (let x = 0; x < W - 30; ) {
    const w = 40 + r() * 60;
    const h = 120 + r() * 200;
    sh.rect(x, H - h, w, h, '#232d55');
    windows(sh, r, x, H - h, w, H, { odds: 0.07, tone: COOL });
    x += w + r() * 20;
  }
  // a lattice tower: a tapering frame with platforms and a mast
  const tx = 1500;
  const base = 120;
  const tall = 400;
  sh.poly([[tx - base / 2, H], [tx - 9, H - tall + 60], [tx + 9, H - tall + 60], [tx + base / 2, H]], '#1b2443');
  sh.poly([[tx - base / 2 + 18, H], [tx - 4, H - tall + 120], [tx + 4, H - tall + 120], [tx + base / 2 - 18, H]], '#2a3463');
  sh.rect(tx - 26, H - 170, 52, 14, '#1b2443');
  sh.rect(tx - 15, H - 290, 30, 10, '#1b2443');
  sh.rect(tx - 2, H - tall, 4, 70, '#1b2443');
  for (const y of [H - tall + 2, H - 288, H - 166]) sh.glow(tx, y, 3, '#ff4a3a');
  sh.light(tx - 24, H - 166, 48, 4, '#ffd48a', 0.8);
  // the front row
  for (let x = 6; x < W - 60; ) {
    const w = 46 + r() * 74;
    const h = 90 + r() * 230;
    if (Math.abs(x + w / 2 - tx) < base / 2 + w / 2 && h > 200) {
      x += w;
      continue;
    }
    const y = H - h;
    const kind = r();
    sh.rect(x, y, w, h, '#1b2443');
    if (kind < 0.18) {
      // stepped top
      sh.rect(x + w * 0.15, y - 26, w * 0.7, 26, '#1b2443');
      sh.rect(x + w * 0.32, y - 48, w * 0.36, 22, '#1b2443');
    } else if (kind < 0.32) {
      // spire
      sh.poly([[x + w * 0.3, y], [x + w / 2, y - 70 - r() * 40], [x + w * 0.7, y]], '#1b2443');
    } else if (kind < 0.5) {
      // mast with an aircraft light
      const mh = 30 + r() * 40;
      sh.rect(x + w / 2 - 1.5, y - mh, 3, mh, '#1b2443');
      sh.glow(x + w / 2, y - mh, 2.5, '#ff4a3a');
    } else if (kind < 0.62) {
      // slanted roof
      sh.poly([[x, y], [x + w, y - 34], [x + w, y]], '#1b2443');
    } else if (kind < 0.72) {
      // water tank
      sh.rect(x + w * 0.2, y - 18, 22, 18, '#1b2443');
      sh.rect(x + w * 0.2 + 2, y - 26, 18, 8, '#1b2443', ' rx="5"');
    }
    const cool = r() < 0.4;
    windows(sh, r, x, y, w, H, { odds: cool ? 0.22 : 0.16, tone: cool ? COOL : WARM });
    if (h > 240 && r() < 0.5) sh.light(x + 4, y + 6, w - 8, 3, pickOf(r, COOL), 0.55);
    x += w + 2 + r() * 10;
  }
  return sh.done(W, H);
}

/** Rolling hills with tree clumps. */
function hills(): string {
  const r = rng(23);
  const W = 1500;
  const H = 300;
  let d = `M0 ${H} L0 ${H - 90}`;
  const pts = 10;
  for (let i = 1; i <= pts; i++) {
    const x = (W / pts) * i;
    const y = i === pts ? H - 90 : H - (80 + r() * 120);
    d += ` Q ${(x - W / pts / 2).toFixed(0)} ${(y - 40 - r() * 30).toFixed(0)} ${x.toFixed(0)} ${y.toFixed(0)}`;
  }
  d += ` L${W} ${H} Z`;
  let body = `<path d="${d}" fill="#121a35"/>`;
  for (let i = 0; i < 26; i++) {
    const x = 20 + r() * (W - 40);
    const s = 18 + r() * 26;
    body += `<ellipse cx="${x.toFixed(0)}" cy="${(H - 70 - r() * 80).toFixed(0)}" rx="${s.toFixed(0)}" ry="${(s * 1.2).toFixed(0)}" fill="#0f1730"/>`;
  }
  return uri(svg(W, H, body));
}

/** Width of one stretch of town along the line, and how many different stretches there are. */
const DISTRICT = 1600;
const TOWN_H = 260;
const INK = '#0b1126';
const ROOF = '#090d1e';

type District = (sh: Sheet, r: () => number, x0: number) => void;

function streetLamp(sh: Sheet, x: number) {
  sh.rect(x, TOWN_H - 100, 3, 100, INK);
  sh.glow(x + 1.5, TOWN_H - 104, 7, '#ffd9a0');
}

function tree(sh: Sheet, r: () => number, x: number, size = 1) {
  const s = (18 + r() * 16) * size;
  sh.rect(x - 2, TOWN_H - s * 1.4, 4, s * 1.4, INK);
  sh.body += `<ellipse cx="${x.toFixed(0)}" cy="${(TOWN_H - s * 1.6).toFixed(0)}" rx="${s.toFixed(0)}" ry="${(s * 0.9).toFixed(0)}" fill="#0a1022"/>`;
  sh.body += `<ellipse cx="${(x + s * 0.5).toFixed(0)}" cy="${(TOWN_H - s * 1.3).toFixed(0)}" rx="${(s * 0.7).toFixed(0)}" ry="${(s * 0.6).toFixed(0)}" fill="#0a1022"/>`;
}

/** Mid-rise offices: lit floors in strips, rooftop billboards. */
const offices: District = (sh, r, x0) => {
  for (let x = x0 + 20; x < x0 + DISTRICT - 160; ) {
    const w = 110 + r() * 90;
    const h = 150 + r() * 80;
    const y = TOWN_H - h;
    sh.rect(x, y, w, h, INK);
    for (let fy = y + 14; fy < TOWN_H - 20; fy += 16) {
      if (r() < 0.55) sh.light(x + 6, fy, w - 12, 6, pickOf(r, COOL), 0.35 + r() * 0.45);
    }
    if (r() < 0.45) {
      sh.rect(x + w * 0.2, y - 34, 4, 34, INK);
      sh.rect(x + w * 0.75 - 4, y - 34, 4, 34, INK);
      sh.light(x + w * 0.15, y - 44, w * 0.7, 22, pickOf(r, NEON), 0.75);
    }
    x += w + 10 + r() * 30;
  }
};

/** A shopping street: narrow two- and three-storey shops, vertical neon signs, lanterns. */
const shops: District = (sh, r, x0) => {
  for (let x = x0 + 10; x < x0 + DISTRICT - 90; ) {
    const w = 56 + r() * 40;
    const h = 70 + r() * 60;
    const y = TOWN_H - h;
    sh.rect(x, y, w, h, INK);
    if (r() < 0.5) sh.rect(x - 3, y - 6, w + 6, 6, ROOF);
    // awning
    sh.poly([[x - 4, TOWN_H - 34], [x + w + 4, TOWN_H - 34], [x + w + 10, TOWN_H - 22], [x - 10, TOWN_H - 22]], ROOF);
    sh.light(x + 4, TOWN_H - 20, w - 8, 16, '#ffcf8a', 0.75);
    windows(sh, r, x, y, w, TOWN_H - 40, { dx: 22, dy: 26, ww: 12, wh: 14, odds: 0.6 });
    if (r() < 0.6) {
      const sx = r() < 0.5 ? x + 4 : x + w - 18;
      const sy = y + 8 + r() * 20;
      sh.rect(sx, sy, 14, 54, INK);
      sh.light(sx + 2, sy + 2, 10, 50, pickOf(r, NEON), 0.85);
    }
    x += w + 2 + r() * 6;
  }
  for (let x = x0 + 30; x < x0 + DISTRICT - 30; x += 46 + r() * 30) sh.glow(x, TOWN_H - 40, 4, '#ff6a4d');
};

/** Houses: gabled roofs, small windows, trees between. */
const houses: District = (sh, r, x0) => {
  for (let x = x0 + 20; x < x0 + DISTRICT - 120; ) {
    const w = 70 + r() * 50;
    const h = 40 + r() * 40;
    const y = TOWN_H - h;
    sh.rect(x, y, w, h, INK);
    const peak = 26 + r() * 18;
    sh.poly([[x - 8, y], [x + w / 2, y - peak], [x + w + 8, y]], ROOF);
    for (let wx = x + 12; wx < x + w - 16; wx += 30) {
      if (r() < 0.6) sh.light(wx, y + 12, 12, 12, pickOf(r, WARM), 0.6 + r() * 0.35);
    }
    sh.rect(x - 14, TOWN_H - 14, w + 28, 14, ROOF);
    x += w + 20 + r() * 30;
    if (r() < 0.5) {
      tree(sh, r, x + 10);
      x += 40;
    }
    if (r() < 0.3) {
      streetLamp(sh, x);
      x += 30;
    }
  }
};

/** A river with a steel truss bridge across it, and the lights on the water. */
const river: District = (sh, r, x0) => {
  const bx = x0 + 240;
  const bw = 1100;
  sh.rect(x0, TOWN_H - 22, DISTRICT, 22, '#0d1934');
  for (let i = 0; i < 40; i++) {
    sh.light(x0 + r() * DISTRICT, TOWN_H - 18 + r() * 16, 20 + r() * 50, 1.5, pickOf(r, WARM), 0.4);
  }
  // the bridge deck and its truss
  sh.rect(bx, TOWN_H - 64, bw, 8, INK);
  sh.rect(bx, TOWN_H - 110, bw, 5, INK);
  let truss = '';
  for (let t = 0; t <= bw; t += 55) {
    truss += `M${bx + t} ${TOWN_H - 58} L${bx + t + 27.5} ${TOWN_H - 108} L${bx + t + 55} ${TOWN_H - 58} `;
  }
  sh.body += `<path d="${truss}" stroke="${INK}" stroke-width="4" fill="none"/>`;
  for (let p = 0; p <= bw; p += 275) sh.rect(bx + p - 6, TOWN_H - 64, 12, 64, INK);
  for (let p = 0; p <= bw; p += 110) sh.glow(bx + p, TOWN_H - 116, 3, '#ffe2a8');
  // a few low buildings on either bank
  for (const x of [x0 + 20, x0 + 120, bx + bw + 40, bx + bw + 140]) {
    const h = 50 + r() * 50;
    sh.rect(x, TOWN_H - 22 - h, 80, h, INK);
    windows(sh, r, x, TOWN_H - 22 - h, 80, TOWN_H - 22, { dx: 20, dy: 18, ww: 9, wh: 9, odds: 0.5 });
  }
};

/** A shrine in its grove: torii gate, a five-storey pagoda, stone lanterns. */
const shrine: District = (sh, r, x0) => {
  for (let i = 0; i < 16; i++) tree(sh, r, x0 + 40 + r() * (DISTRICT - 80), 1.4 + r() * 0.8);
  const px = x0 + 980;
  let tier = 0;
  for (let y = TOWN_H, w = 120; tier < 5; tier++, w -= 16) {
    const h = 30;
    sh.rect(px - w / 2 + 18, y - h, w - 36, h, INK);
    sh.poly([[px - w / 2 - 6, y - h + 4], [px + w / 2 + 6, y - h + 4], [px + w / 2 - 14, y - h - 10], [px - w / 2 + 14, y - h - 10]], ROOF);
    sh.light(px - 6, y - h + 10, 12, 10, '#ffc872', 0.7);
    y -= h + 10;
  }
  sh.rect(px - 2, TOWN_H - 5 * 40 - 50, 4, 50, INK);
  // torii
  const gx = x0 + 520;
  const red = '#3d1418';
  sh.rect(gx, TOWN_H - 110, 10, 110, red);
  sh.rect(gx + 110, TOWN_H - 110, 10, 110, red);
  sh.poly([[gx - 22, TOWN_H - 118], [gx + 142, TOWN_H - 118], [gx + 150, TOWN_H - 130], [gx - 30, TOWN_H - 130]], red);
  sh.rect(gx - 8, TOWN_H - 96, 136, 8, red);
  for (const lx of [gx + 200, gx + 300, px - 140, px + 120]) {
    sh.rect(lx - 6, TOWN_H - 36, 12, 36, INK);
    sh.rect(lx - 12, TOWN_H - 44, 24, 10, ROOF);
    sh.glow(lx, TOWN_H - 30, 4, '#ffb36a');
  }
};

/** Danchi: long apartment blocks with balconies, a water tower on the roof. */
const danchi: District = (sh, r, x0) => {
  for (let x = x0 + 40; x < x0 + DISTRICT - 280; ) {
    const w = 240 + r() * 100;
    const h = 140 + r() * 40;
    const y = TOWN_H - h;
    sh.rect(x, y, w, h, '#0d142c');
    for (let fy = y + 18; fy < TOWN_H - 12; fy += 20) {
      sh.rect(x, fy + 10, w, 3, ROOF);
      for (let wx = x + 8; wx < x + w - 16; wx += 18) {
        if (r() < 0.32) sh.light(wx, fy, 10, 9, pickOf(r, r() < 0.7 ? WARM : COOL), 0.5 + r() * 0.45);
      }
    }
    sh.rect(x + w - 60, y - 22, 30, 22, INK);
    sh.rect(x + w - 64, y - 30, 38, 8, INK);
    x += w + 50 + r() * 60;
    if (r() < 0.6) tree(sh, r, x - 25);
  }
};

/** A works by the line: sawtooth roofs, chimneys with red lights, a gas holder, a gantry crane. */
const industrial: District = (sh, r, x0) => {
  for (let x = x0 + 20; x < x0 + DISTRICT - 220; ) {
    const w = 160 + r() * 80;
    const h = 50 + r() * 30;
    const y = TOWN_H - h;
    sh.rect(x, y, w, h, INK);
    const pts: [number, number][] = [[x, y]];
    for (let t = x; t < x + w; t += 32) pts.push([t, y - 22], [t + 32, y]);
    sh.poly([...pts, [x + w, y]], ROOF);
    for (let t = x + 6; t < x + w - 20; t += 32) sh.light(t + 2, y - 16, 8, 14, '#d8f0ff', 0.45);
    if (r() < 0.6) {
      const cx = x + w * (0.2 + r() * 0.6);
      const ch = 110 + r() * 60;
      sh.rect(cx - 8, TOWN_H - ch, 16, ch, INK);
      sh.glow(cx, TOWN_H - ch - 2, 2.5, '#ff4a3a');
    }
    x += w + 40 + r() * 50;
  }
  const gx = x0 + DISTRICT - 180;
  sh.rect(gx, TOWN_H - 110, 120, 110, INK, ' rx="40"');
  for (let i = 0; i < 4; i++) sh.rect(gx, TOWN_H - 100 + i * 26, 120, 3, ROOF);
  sh.rect(x0 + 700, TOWN_H - 170, 8, 170, INK);
  sh.rect(x0 + 880, TOWN_H - 170, 8, 170, INK);
  sh.rect(x0 + 640, TOWN_H - 178, 300, 12, INK);
  sh.glow(x0 + 645, TOWN_H - 180, 2.5, '#ffb01f');
  sh.glow(x0 + 935, TOWN_H - 180, 2.5, '#ffb01f');
};

/** Out of town: rice paddies, farmhouses with deep roofs, a pylon carrying the lines. */
const fields: District = (sh, r, x0) => {
  for (let i = 0; i < 6; i++) {
    sh.rect(x0, TOWN_H - 30 + i * 5, DISTRICT, 1.5, '#16213f');
  }
  for (let i = 0; i < 40; i++) sh.light(x0 + r() * DISTRICT, TOWN_H - 28 + r() * 26, 12 + r() * 30, 1.2, '#9fb4ff', 0.3);
  for (const fx of [x0 + 160 + r() * 80, x0 + 1100 + r() * 120]) {
    sh.rect(fx, TOWN_H - 46, 110, 46, INK);
    sh.poly([[fx - 24, TOWN_H - 40], [fx + 55, TOWN_H - 104], [fx + 134, TOWN_H - 40]], ROOF);
    sh.light(fx + 20, TOWN_H - 32, 16, 14, '#ffc872', 0.85);
    tree(sh, r, fx + 150, 1.3);
  }
  // a lattice pylon
  const px = x0 + 700;
  let lattice = `M${px - 40} ${TOWN_H} L${px - 8} ${TOWN_H - 220} L${px + 8} ${TOWN_H - 220} L${px + 40} ${TOWN_H} `;
  for (let y = TOWN_H - 20; y > TOWN_H - 210; y -= 30) {
    const half = 8 + ((y - (TOWN_H - 220)) / 220) * 32;
    lattice += `M${px - half} ${y} L${px + half} ${y - 30} M${px + half} ${y} L${px - half} ${y - 30} `;
  }
  sh.body += `<path d="${lattice}" stroke="${INK}" stroke-width="3" fill="none"/>`;
  sh.rect(px - 50, TOWN_H - 200, 100, 5, INK);
  sh.rect(px - 38, TOWN_H - 170, 76, 5, INK);
  sh.body += `<path d="M${x0} ${TOWN_H - 176} Q${px - 350} ${TOWN_H - 150} ${px - 50} ${TOWN_H - 196} M${px + 50} ${TOWN_H - 196} Q${px + 450} ${TOWN_H - 150} ${x0 + DISTRICT} ${TOWN_H - 176}" stroke="#05070d" stroke-width="1.5" fill="none"/>`;
  sh.glow(px, TOWN_H - 224, 2.5, '#ff4a3a');
};

/** A fairground by the water: a big wheel ringed with lights, and a coaster track. */
const fair: District = (sh, r, x0) => {
  const wx = x0 + 520;
  const R = 110;
  const cy = TOWN_H - R - 20;
  sh.body += `<circle cx="${wx}" cy="${cy}" r="${R}" stroke="${INK}" stroke-width="5" fill="none"/><circle cx="${wx}" cy="${cy}" r="${R * 0.4}" stroke="${INK}" stroke-width="3" fill="none"/>`;
  let spokes = '';
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    spokes += `M${wx} ${cy} L${(wx + Math.cos(a) * R).toFixed(1)} ${(cy + Math.sin(a) * R).toFixed(1)} `;
    sh.glow(wx + Math.cos(a) * R, cy + Math.sin(a) * R, 3, NEON[i % NEON.length]!);
    sh.rect(wx + Math.cos(a) * R - 7, cy + Math.sin(a) * R + 4, 14, 12, INK, ' rx="3"');
  }
  sh.body += `<path d="${spokes}" stroke="${INK}" stroke-width="2" fill="none"/>`;
  sh.body += `<path d="M${wx - 70} ${TOWN_H} L${wx} ${cy} L${wx + 70} ${TOWN_H}" stroke="${INK}" stroke-width="7" fill="none"/>`;
  // coaster
  const cx0 = x0 + 760;
  let track = `M${cx0} ${TOWN_H - 40}`;
  for (let t = 0; t < 700; t += 100) {
    track += ` Q${cx0 + t + 50} ${TOWN_H - 160 - r() * 60} ${cx0 + t + 100} ${TOWN_H - 50 - r() * 30}`;
  }
  sh.body += `<path d="${track}" stroke="${INK}" stroke-width="5" fill="none"/>`;
  for (let t = 0; t < 700; t += 40) sh.rect(cx0 + t, TOWN_H - 60, 4, 60, INK);
  for (let x = x0 + 60; x < x0 + DISTRICT; x += 60 + r() * 40) {
    if (Math.abs(x - wx) < 140) continue;
    sh.rect(x, TOWN_H - 40, 40, 40, INK);
    sh.poly([[x - 4, TOWN_H - 40], [x + 20, TOWN_H - 60], [x + 44, TOWN_H - 40]], ROOF);
    sh.light(x + 6, TOWN_H - 30, 28, 10, pickOf(r, NEON), 0.7);
  }
};

/** The harbour: container cranes, stacked boxes, a lighthouse at the end of the mole. */
const harbour: District = (sh, r, x0) => {
  sh.rect(x0, TOWN_H - 18, DISTRICT, 18, '#0d1934');
  for (let i = 0; i < 30; i++) sh.light(x0 + r() * DISTRICT, TOWN_H - 14 + r() * 12, 30 + r() * 40, 1.5, pickOf(r, COOL), 0.35);
  for (let x = x0 + 60; x < x0 + 900; x += 46) {
    const stack = 1 + Math.floor(r() * 4);
    for (let s = 0; s < stack; s++) sh.rect(x, TOWN_H - 18 - (s + 1) * 16, 42, 14, s % 2 ? '#101a36' : INK);
  }
  for (const cx of [x0 + 250, x0 + 650]) {
    sh.rect(cx - 50, TOWN_H - 190, 8, 172, INK);
    sh.rect(cx + 42, TOWN_H - 190, 8, 172, INK);
    sh.rect(cx - 120, TOWN_H - 200, 280, 12, INK);
    sh.rect(cx - 20, TOWN_H - 236, 6, 36, INK);
    sh.glow(cx - 17, TOWN_H - 238, 2.5, '#ff4a3a');
    sh.glow(cx + 150, TOWN_H - 196, 2.5, '#ffb01f');
  }
  const lx = x0 + 1300;
  sh.rect(x0 + 1000, TOWN_H - 26, 360, 10, INK);
  sh.poly([[lx - 16, TOWN_H - 26], [lx - 9, TOWN_H - 150], [lx + 9, TOWN_H - 150], [lx + 16, TOWN_H - 26]], '#141d3a');
  sh.rect(lx - 12, TOWN_H - 170, 24, 20, INK);
  sh.glow(lx, TOWN_H - 160, 6, '#fff3c4');
  sh.lit += `<polygon points="${lx},${TOWN_H - 160} ${lx - 300},${TOWN_H - 190} ${lx - 300},${TOWN_H - 140}" fill="#fff3c4" opacity=".12"/>`;
};

/**
 * Rooftops close to the line. Not one block repeated: a run of different
 * districts, so each stretch between stops passes somewhere new. The city
 * thins out as the night goes on, and the harbour and fairground are by the
 * water at the end of the line.
 */
function town() {
  const order: District[] = [offices, shops, houses, river, shrine, danchi, industrial, fields, fair, harbour];
  const sh = new Sheet();
  order.forEach((draw, i) => {
    const r = rng(37 + i * 101);
    draw(sh, r, i * DISTRICT);
  });
  return sh.done(order.length * DISTRICT, TOWN_H);
}

/** A utility pole every 1100px, with wires that carry on across the seam. */
function poles(): string {
  const W = 1100;
  const H = 700;
  const pole = W / 2;
  // One span runs pole -> next pole (W px). Half of it falls on this tile's
  // right side and the other half re-enters on the left, so the wire is
  // continuous across the seam between tiles.
  const wire = (y0: number, sag: number) => {
    const at = (t: number) => y0 + sag * 4 * t * (1 - t);
    const run = (t0: number, t1: number, shift: number) => {
      let d = '';
      for (let i = 0; i <= 12; i++) {
        const t = t0 + ((t1 - t0) * i) / 12;
        d += `${i === 0 ? 'M' : 'L'}${(pole + t * W - shift).toFixed(1)} ${at(t).toFixed(1)} `;
      }
      return d;
    };
    return `<path d="${run(0, 0.5, 0)}${run(0.5, 1, W)}" stroke="#05070d" stroke-width="2" fill="none"/>`;
  };
  const body =
    `<rect x="${pole - 7}" y="40" width="14" height="${H - 40}" fill="#05070d"/>` +
    `<rect x="${pole - 70}" y="70" width="140" height="8" fill="#05070d"/>` +
    `<rect x="${pole - 50}" y="120" width="100" height="6" fill="#05070d"/>` +
    wire(74, 60) +
    wire(123, 52);
  return uri(svg(W, H, body));
}

const sky = skyline();
const near = town();

export const tiles = {
  skyline: sky.body,
  skylineLit: sky.lit,
  hills: hills(),
  town: near.body,
  townLit: near.lit,
  poles: poles(),
};

/** Distance between two stops along the line, in world pixels. */
export const SPAN = 3600;
/** How long the train takes between stops. */
export const RIDE_MS = 3600;
